import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { httpHeader } from 'global/httpHeader';
import { BrowserCasbinService } from './casbin/browser-casbin.service';

interface UserScopesResponse {
  code: string;
  status: string;
  message: string;
  data: Array<any>;
}

@Injectable({
  providedIn: 'root'
})
export class UserScopeService {
  private baseUrl = environment.apiLink;
  userScopes$ = new BehaviorSubject<any[]>([]);

  constructor(
    private http: HttpClient,
    private casbinService: BrowserCasbinService
  ) {}

  /**
   * Load user scopes from API and initialize Casbin
   */
  loadUserScopes(organizationId?: string, userId?: string): Observable<any[]> {
    
    const url = `${this.baseUrl}iam/organizations/${organizationId}/users/${userId}/scopes`;
    return this.http.get<UserScopesResponse>(url, httpHeader.httpOptions).pipe(
      map(response => {
        if (response.code === '200' && response.status === 'SUCCESS') {
          return response.data;
        } else {
          throw new Error(`Failed to load user scopes: ${response.message}`);
        }
      }),
      tap(async (scopes) => {

        // Update local state
        this.userScopes$.next(scopes);

        // Initialize Casbin with the loaded scopes
        if (userId && organizationId) {
          try {
            await this.casbinService.initialize(userId, organizationId, scopes);
          } catch (error) {
            console.error('❌ Failed to initialize Casbin:', error);
          }
        }
      })
    );
  }



  /**
   * Determine user role based on scopes
   */
  private determineUserRole(scopes: any[]): string {
    // Check for admin scope
    if (scopes.some(scope => scope.name === 'all')) {
      return 'admin';
    }

    // Check for specific role patterns
    const scopeNames = scopes.map(s => s.name);

    if (scopeNames.some(name => name.includes('manager'))) {
      return 'manager';
    }

    if (scopeNames.some(name => name.includes('supervisor'))) {
      return 'supervisor';
    }

    if (scopeNames.some(name => name.includes('sales') || name.includes('sale'))) {
      return 'sales_representative';
    }

    // Default role
    return 'user';
  }

  /**
   * Get current user scopes
   */
  getCurrentScopes(): Observable<any[]> {
    return this.userScopes$.asObservable();
  }

  /**
   * Check if user has specific scope
   */
  hasScope(scopeName: string): boolean {
    return this.userScopes$.value.some(scope => scope.name === scopeName);
  }

  /**
   * Get scopes by pattern
   */
  getScopesByPattern(pattern: string): any[] {
    const regex = new RegExp(pattern, 'i');
    return this.userScopes$.value.filter(scope => regex.test(scope.name));
  }

  /**
   * Clear user scopes (on logout)
   */
  async clearUserScopes(): Promise<void> {
    this.userScopes$.next([]);
    this.casbinService.clear();
  }

  /**
   * Refresh user scopes
   */
  async refreshUserScopes(organizationId?: string, userId?: string): Promise<any[]> {
    try {
      const scopes = await this.loadUserScopes(organizationId, userId).toPromise();
      return scopes || [];
    } catch (error) {
      console.error('Failed to refresh user scopes:', error);
      return [];
    }
  }
}
